import SettingsIcon from '@mui/icons-material/Settings';
import { kvoxAskDestinationRoutes, kvoxAskDestinations, } from '../ask/ui/index.js';
import { kvoxHomeDestination, kvoxHomeDestinationRoutes } from '../home/ui/index.js';
import { kvoxGraphDestinationRoutes, kvoxGraphDestinations, kvoxReviewDestinationRoutes, kvoxReviewDestinations, } from '../graph/ui/index.js';
import { kvoxNoteTemplatesDestinationRoutes, kvoxNoteTemplatesDestinations, kvoxNotesDestinationRoutes, kvoxNotesDestinations, } from '../notes/ui/index.js';
import { kvoxTranscriptsDestinationRoutes, kvoxTranscriptsDestinations, } from '../transcripts/ui/index.js';
/**
 * A slice's destinations as the platform's. They are structurally the same type
 * except for `feature`: a slice names the `graph` feature as a plain string, while
 * the platform types it as the union the HOST declares by augmenting
 * `SettingsFeatureRegistry` and registering a resolver (`registerSettingsFeature`).
 * With no `graph` registration the platform fails closed and hides the row.
 */
function asShell(destinations) {
    return destinations;
}
const DEFAULT_INCLUDE = ['transcripts', 'notes', 'graph'];
const bottomBarCache = new WeakMap();
/**
 * The navigation the phone bottom bar draws: the given one with the pinned
 * destinations (the modes: Settings, Console) taken out.
 *
 * @remarks
 * KVox's rule since #106: a pinned destination is a mode and never a peer on the
 * phone bar. The platform's bar draws pinned destinations too (at the end), so the
 * filter is data, not a breakpoint: `ShellBottomNav` keeps its own `down('sm')`
 * self-gate and there are still exactly five gates. The result is cached per input,
 * so the same navigation always yields the same object (the shell keys on identity).
 *
 * @param navigation - the host's navigation.
 * @returns the same navigation with only its non-pinned destinations.
 *
 * @stability experimental
 */
export function withoutPinned(navigation) {
    const cached = bottomBarCache.get(navigation);
    if (cached)
        return cached;
    const filtered = {
        ...navigation,
        destinations: navigation.destinations.filter((destination) => !destination.pinned),
    };
    bottomBarCache.set(navigation, filtered);
    return filtered;
}
/**
 * Composes the KVox navigation: the host's own destinations plus the ones the
 * KVox slices declare, with the route ownership that makes the right row light up.
 *
 * @remarks
 * The slice rows are the slices' own data (`kvoxNotesDestinations` and friends), so
 * their labels, icons, paths and gates (`permission`, `feature: 'graph'`) live in
 * the slices; this only orders them. A row with `feature: 'graph'` is hidden by the
 * shell unless the host's `graph` settings feature (`registerSettingsFeature`) says
 * on: no host-side `if`. Declare the result ONCE at module scope: the shell keys on
 * its identity.
 *
 * @param options - see {@link CreateKvoxNavigationOptions}.
 * @returns the navigation and its phone-bar twin.
 *
 * @example
 * ```ts
 * // Desktop, a local single-user host.
 * export const { navigation } = createKvoxNavigation({
 *   include: ['notes', 'graph', 'review', 'ask', 'templates'],
 *   settingsPath: '/settings',
 *   settingsSections: DESKTOP_SETTINGS_SECTIONS,
 * });
 * ```
 *
 * @stability experimental
 */
export function createKvoxNavigation(options = {}) {
    const include = new Set(options.include ?? DEFAULT_INCLUDE);
    const extra = options.extra ?? [];
    const slices = [];
    const routes = {};
    if (include.has('home')) {
        slices.push(...asShell([kvoxHomeDestination]));
        Object.assign(routes, kvoxHomeDestinationRoutes);
    }
    if (include.has('transcripts')) {
        slices.push(...asShell(kvoxTranscriptsDestinations));
        Object.assign(routes, kvoxTranscriptsDestinationRoutes);
    }
    if (include.has('notes')) {
        slices.push(...asShell(kvoxNotesDestinations));
        Object.assign(routes, kvoxNotesDestinationRoutes);
    }
    if (include.has('graph')) {
        slices.push(...asShell(kvoxGraphDestinations));
        Object.assign(routes, kvoxGraphDestinationRoutes);
    }
    if (include.has('review')) {
        slices.push(...asShell(kvoxReviewDestinations));
        Object.assign(routes, kvoxReviewDestinationRoutes);
    }
    if (include.has('ask')) {
        slices.push(...asShell(kvoxAskDestinations));
        Object.assign(routes, kvoxAskDestinationRoutes);
        // `/ask` belongs to the Ask row now: one row lights up, never two.
        if (routes.knowledge) {
            routes.knowledge = routes.knowledge.filter((prefix) => prefix !== '/ask');
        }
    }
    if (include.has('templates')) {
        slices.push(...asShell(kvoxNoteTemplatesDestinations));
        Object.assign(routes, kvoxNoteTemplatesDestinationRoutes);
    }
    const settingsRow = options.settingsPath !== undefined && !extra.some((destination) => destination.key === 'settings')
        ? [
            {
                key: 'settings',
                label: options.settingsLabel ?? 'Settings',
                compactLabel: options.settingsLabel ?? 'Settings',
                Icon: SettingsIcon,
                path: options.settingsPath,
                pinned: true,
            },
        ]
        : [];
    const destinations = [
        ...extra.filter((destination) => !destination.pinned),
        ...slices,
        ...settingsRow,
        ...extra.filter((destination) => destination.pinned),
    ];
    // The settings row owns its hub; the extras own what they say, or their own path.
    const destinationRoutes = { ...routes };
    for (const row of settingsRow)
        destinationRoutes[row.key] = [row.path];
    for (const destination of extra) {
        destinationRoutes[destination.key] =
            options.extraRoutes?.[destination.key] ?? [
                destination.path,
            ];
    }
    const settingsSurfaces = options.settingsSurfaces ??
        (options.settingsSections && options.settingsPath !== undefined
            ? [
                {
                    sections: options.settingsSections,
                    hubPath: options.settingsPath,
                    hubTitle: options.settingsLabel ?? 'Settings',
                },
            ]
            : undefined);
    const navigation = {
        destinations,
        destinationRoutes,
        ...(settingsSurfaces ? { settingsSurfaces } : {}),
        ...(options.console ? { console: options.console } : {}),
        ...(options.homePath !== undefined ? { homePath: options.homePath } : {}),
        ...(options.useRailPreference ? { useRailPreference: options.useRailPreference } : {}),
    };
    return {
        navigation: navigation,
        bottomBarNavigation: withoutPinned(navigation),
    };
}
